"use client"

import { Card } from "@/shared/components/ui/card"
import {
  Target,
  Users,
  Award,
  TrendingUp,
  ShieldCheck,
  Zap,
  Cloud,
  BarChart3,
  Headphones,
  Puzzle,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { Button } from "@/shared/components/ui/button"

export function SobreNosotrosPage() {
  const [isVisible, setIsVisible] = useState(false)
  const cardsRef = useRef<(HTMLDivElement | null)[]>([])
  const [visibleCards, setVisibleCards] = useState<boolean[]>([false, false, false, false])

  useEffect(() => {
    setIsVisible(true)
    const observers = cardsRef.current.map((card, index) => {
      if (!card) return null
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleCards((prev) => {
              const newState = [...prev]
              newState[index] = true
              return newState
            })
          }
        },
        { threshold: 0.1 }
      )
      observer.observe(card)
      return observer
    })
    return () => {
      observers.forEach((observer) => observer?.disconnect())
    }
  }, [])

  const values = [
    {
      icon: Target,
      title: "Nuestra Misión",
      description:
        "Facilitar la facturación electrónica y el cumplimiento normativo para el sector salud, proporcionando herramientas intuitivas, seguras y eficientes que optimicen los procesos administrativos de nuestros clientes.",
    },
    {
      icon: TrendingUp,
      title: "Nuestra Visión",
      description:
        "Ser la plataforma líder en facturación electrónica para el sector salud en Colombia, reconocidos por nuestra innovación, confiabilidad y excelente servicio al cliente.",
    },
    {
      icon: Users,
      title: "Nuestro Equipo",
      description:
        "Contamos con un equipo multidisciplinario de profesionales comprometidos con la excelencia, la responsabilidad y el trabajo en equipo, siempre enfocados en superar las expectativas de nuestros clientes.",
    },
    {
      icon: Award,
      title: "Nuestros Valores",
      list: [
        "Compromiso con la excelencia",
        "Responsabilidad y confiabilidad",
        "Innovación constante",
        "Trabajo en equipo",
      ],
    },
  ]

  const motivos = [
    {
      icono: ShieldCheck,
      titulo: "Cumplimiento garantizado",
      texto: "Cumple con la DIAN y el Ministerio de Salud sin preocuparte por actualizaciones normativas.",
    },
    {
      icono: Zap,
      titulo: "Automatización total",
      texto: "Procesos automáticos que reducen errores y tiempos de gestión en facturación y RIPS.",
    },
    {
      icono: Cloud,
      titulo: "Seguridad en la nube",
      texto: "Tus datos protegidos con cifrado, copias de seguridad y servidores certificados.",
    },
    {
      icono: BarChart3,
      titulo: "Monitoreo inteligente",
      texto: "Consulta en tiempo real el estado de tus facturas y reportes personalizados.",
    },
    {
      icono: Headphones,
      titulo: "Soporte especializado",
      texto: "Asistencia 8/5 con expertos en el sector salud y facturación electrónica.",
    },
    {
      icono: Puzzle,
      titulo: "Integración fácil",
      texto: "Conecta tu sistema de historias clínicas electrónicas sin complicaciones.",
    },
  ]

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Patrón de fondo */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:4rem_4rem] pointer-events-none" />

      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary/5 rounded-full blur-3xl" />

      {/* Sección tipo Hero con texto + imagen */}
      <section className="relative py-32 lg:py-40 container mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        {/* Lado izquierdo: texto */}
        <div
          className={`space-y-8 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight">
            Sobre <span className="text-primary">Nosotros</span>
          </h1>
          <p className="text-xl lg:text-2xl text-muted-foreground leading-relaxed max-w-2xl">
            Somos <span className="font-semibold text-primary">Valerosas Tech S.A.S</span>, creadores de Rapyfact. 
            Desarrollamos soluciones tecnológicas que impulsan la eficiencia del sector salud colombiano 
            mediante facturación electrónica, validación RIPS y automatización de procesos.
          </p>
          <div>
            <a href="https://app.rapyfact.com/register" target="_blank" rel="noopener noreferrer">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-7 shadow-lg"
              >
                Conoce más
              </Button>
            </a>
          </div>
        </div>

        {/* Lado derecho: imagen */}
        <div
          className={`relative transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "200ms" }}
        >
          <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
            <img
              src="/images/image2.png"
              alt="Equipo Rapyfact"
              className="w-full h-full object-contain object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/10 to-transparent pointer-events-none" />
          </div>
        </div>
      </section>

      {/* Historia */}
      <div className="container mx-auto px-4 lg:px-8 mt-12">
        <Card
          className={`p-8 lg:p-12 border-border/50 transition-all duration-700 delay-200 ${
            isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
          }`}
        >
          <h2 className="text-3xl font-bold mb-6">Nuestra Historia</h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-4">
            Rapyfact nace de la necesidad de simplificar los procesos de facturación electrónica en el sector salud colombiano.
            Con experiencia tecnológica y conocimiento regulatorio, construimos una plataforma que combina facilidad, seguridad y cumplimiento total.
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Nuestro equipo está conformado por profesionales de tecnología, facturación y salud, comprometidos en ofrecer soluciones confiables e innovadoras.
          </p>
        </Card>
      </div>

      {/* Valores */}
      <div className="container mx-auto px-4 lg:px-8 mt-16">
        <div className="grid md:grid-cols-2 gap-8">
          {values.map((value, index) => {
            const Icon = value.icon
            return (
              <div key={index} ref={(el) => {
  cardsRef.current[index] = el
}}
>
                <Card
                  className={`p-8 space-y-4 border-border/50 h-full transition-all duration-700 ${
                    visibleCards[index]
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-8"
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold">{value.title}</h3>
                  {value.description && (
                    <p className="text-muted-foreground leading-relaxed text-[17px]">
                      {value.description}
                    </p>
                  )}
                  {value.list && (
                    <ul className="text-muted-foreground leading-relaxed text-[17px] space-y-2">
                      {value.list.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-primary mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>
            )
          })}
        </div>
      </div>

      {/* Sección: ¿Por qué Rapyfact? */}
      {/* Sección: ¿Por qué Rapyfact? */}
<section className="py-24 bg-gradient-to-b from-white to-gray-50 mt-32">
  <div className="container mx-auto px-4 lg:px-8 text-center">
    {/* Título principal */}
    <h2 className="text-4xl md:text-5xl font-bold mb-6">
      ¿Por qué <span className="text-primary">Rapyfact?</span>
    </h2>

    <p className="text-gray-600 text-lg max-w-3xl mx-auto mb-16">
      Simplificamos tus procesos, garantizamos cumplimiento normativo y
      ofrecemos acompañamiento humano especializado.
    </p>

    {/* Tarjetas de motivos */}
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 mb-24">
      {motivos.map(({ icono: Icon, titulo, texto }, i) => (
        <div
          key={i}
          className="group relative p-10 rounded-3xl bg-white shadow-md hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-500 min-h-[260px] flex flex-col items-center justify-start text-center border border-gray-100"
        >
          <div className="w-16 h-16 mb-5 flex items-center justify-center rounded-full bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors duration-300">
            <Icon className="w-8 h-8" strokeWidth={1.8} />
          </div>
          <h3 className="text-xl font-semibold mb-3 text-gray-800">{titulo}</h3>
          <p className="text-gray-600 leading-relaxed text-base">{texto}</p>
        </div>
      ))}
    </div>

    {/* Llamado a la acción dentro de la misma sección */}
    <div className="max-w-7xl mx-auto bg-gradient-to-r from-sky-50 to-white rounded-3xl shadow-xl border border-gray-100 text-center p-16 md:p-20 mt-16">
      <h3 className="text-3xl md:text-4xl font-bold mb-4">
        ¿Qué esperas para probarlo?
      </h3>
      <p className="text-lg md:text-xl mb-8 opacity-90">
        <span className="font-semibold">Rapyfact</span> agiliza tus procesos de
        facturación electrónica con precisión, seguridad y total cumplimiento
        normativo.
      </p>
      <a
        href="https://app.rapyfact.com/register"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button
          size="lg"
          className="[#06A1FF] text-white font-semibold text-lg px-8 py-4 rounded-full shadow-md hover:bg-sky-700 hover:scale-105 transition-all duration-300"
        >
          Pruébalo ahora
        </Button>
      </a>
    </div>
  </div>
</section>

    </div>
  )
}
